// node module import
import React, { Component } from "react";
import styled, { ThemeProvider } from "styled-components";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { ApolloProvider } from "react-apollo";

// project import
import ServerSync from "module-common/ServerSyncWithStoreQuerySubs";
import ErrorScreen from "module-common/ErrorScreen";
import LoadingPanel from "module-common/LoadingPanel";
import AbsoluteFlex from "module-common/AbsoluteFlex";
import NotificationBox from "module-common/NotificationBox";
import { GetClient, CheckIsLoggedIn } from "Apollo";
import { CommonStore, IsSupervisor } from "Store";
import { CurrentTheme, ChangeTheme } from "theme";

// modules
import SideMenu from "module-sidemenu/ModuleSideMenu";
import Login from "module-login/ModuleLoginWithMutation";
import CaseManagement from "module-case-management/ModuleCaseManagement";
import SupervisorSetting from "module-supervisor-setting/ModuleSupervisorSetting";
import CaseHistory from "module-case-history/ModuleCaseHistory";
import Analytics from "module-analytics";
import AgentManagement from "module-agent-management/ModuleAgentManagementWithQueryMutation";
import Trial from "module-trial";
import Schedule from "module-schedule/ModuleScheduleWithQueryMutation";
import EmailCampaign from "module-email-campaign/ModuleEmailCampaignWithQueryMutation"
import BulkSms from "module-bulk-sms/ModuleBulkSmsWithQueryMutation"
import CustomerJourney from "module-customer-journey/ModuleCustomerJourney";
import FirstTime from "module-first-time/ModuleFirstTimeWithQueryMutation";
import ConnectAccount from "module-first-time/connect-account/ConnectAccount";
import { IsBulkSmsEnabled, IsEmailCampaignEnabled } from "./Store";
import { LoadCompanyConfiguration } from "./Apollo";

// config for modules
const ModuleConfig = [
  {
    component: CaseManagement,
    tooltip: "Case Management",
    path: "/case-management",
    icon: "sidebar-case",
    iconSelected: "sidebar-case-selected"
  },
  {
    component: AgentManagement,
    tooltip: "Profile",
    path: "/profile",
    icon: "sidebar-agent",
    iconSelected: "sidebar-agent-selected"
  },
  {
    component: CaseHistory,
    tooltip: "Case History",
    path: "/case-history",
    icon: "sidebar-history",
    iconSelected: "sidebar-history-selected"
  },
  {
    component: CustomerJourney,
    tooltip: "Customer Journey",
    path: "/customer-journey",
    icon: "sidebar-customer-management",
    iconSelected: "sidebar-customer-management-selected"
  },
  // {
  //   component: Schedule,
  //   tooltip: "Schedule",
  //   path: "/schedule",
  //   icon: "sidebar-schedule",
  //   iconSelected: "sidebar-schedule-selected"
  // },
  {
    component: EmailCampaign,
    tooltip: "Email Campaign",
    path: "/email-campaign",
    icon: "sidebar-email",
    iconSelected: "sidebar-email-selected",
    restrictUser: true,
    willShow: () => IsEmailCampaignEnabled()
  },
  {
    component: BulkSms,
    tooltip: "Bulk SMS",
    path: "/bulk-sms",
    icon: "sidebar-sms",
    iconSelected: "sidebar-sms-selected",
    restrictUser: true,
    willShow: () => IsBulkSmsEnabled()
  },
  {
    component: SupervisorSetting,
    tooltip: "Supervisor Setting",
    path: "/supervisor-setting",
    icon: "sidebar-supervisor",
    iconSelected: "sidebar-supervisor-selected",
    restrictSupervisor: true
  },
  {
    component: Analytics,
    tooltip: "Analytics",
    path: "/analytics",
    icon: "sidebar-analytic",
    iconSelected: "sidebar-analytic-selected",
    restrictSupervisor: true,
    customBorder: true
  }
];

export default observer(
  class App extends Component {
    states = observable({ isLoading: true, isAuthorized: false });

    componentWillReact() {
      // if we got data from server, then change theme according to data
      if (CommonStore.me) {
        ChangeTheme(CommonStore.me.company.theme);
      }
    }

    async componentDidMount() {
      this.states.isAuthorized = await CheckIsLoggedIn();
      if(this.states.isAuthorized) await LoadCompanyConfiguration()
      this.states.isLoading = false;
    }

    render() {
      const { isLoading, isAuthorized } = this.states;
      const isSupervisor = IsSupervisor();
      const { moduleLoaded } = CommonStore
      return (
        <ApolloProvider client={GetClient()}>
          <ThemeProvider theme={CurrentTheme}>
            <BrowserRouter>
              <Switch>
                {(isLoading && !moduleLoaded) && (
                  <Route
                    render={() => (
                      <AbsoluteFlex>
                        <LoadingPanel />
                      </AbsoluteFlex>
                    )}
                  />
                )}

                <Route path="/404" render={() => <ErrorScreen code={404} />} />
                <Route path="/401" render={() => <ErrorScreen code={401} />} />
                <Route path="/502" render={() => <ErrorScreen code={502} />} />
                <Route path="/trial" component={Trial} />
                <Route
                  path="/setup-account"
                  render={() =>
                    isAuthorized ? <Redirect to="/" /> : <FirstTime />
                  }
                />
                <Route
                  path="/connect-account"
                  render={() =>
                    isAuthorized ? <ConnectAccount /> : <Redirect to="/" />
                  }
                />
                <Route
                  path="/login"
                  render={() =>
                    isAuthorized ? <Redirect to="/" /> : <Login />
                  }
                />    
                {!isAuthorized && !isLoading && <Route path="/" render={() => <Redirect to="/login" />} />}
                {isAuthorized && moduleLoaded &&
                <Route
                  render={() => {                  
                    return <Wrapper>  
                      <ServerSync />   
                      <NotificationBox />
                      {CommonStore.me && [
                        <SideMenu
                          key="sidemenu"
                          config={ModuleConfig.filter(mod => {
                            if(mod.willShow) {
                              console.log(mod.willShow(), mod.path)
                              return mod.willShow()
                            }
                            else return true
                          })}
                          agent={CommonStore.me.agent}
                          isSupervisor={isSupervisor}
                        />,
                        <Mod key="mod">
                          <Switch>
                            {ModuleConfig.map((mod, index) => (
                              <Route
                                key={mod.path}
                                path={mod.path}
                                render={props => {
                                  let comp = <mod.component {...props} />;
                                  if (!mod.customBorder) {
                                    comp = <Border>{comp}</Border>;
                                  }
                                  if(mod.restrictUser) {
                                    const isAllowed = mod.willShow()
                                    if(!isAllowed) return <Redirect to="/401" />
                                  }
                                  if (mod.restrictSupervisor) {
                                    if (isSupervisor) {
                                      return comp;
                                    } else {
                                      return <Redirect to="/401" />;
                                    }
                                  } else {
                                    return comp;
                                  }
                                }}
                              />
                            ))}
                            )};
                            <Route exact path="/">
                              <Redirect to="/case-management" />
                            </Route>
                            <Redirect to="/404" />
                          </Switch>
                        </Mod>
                      ]}
                    </Wrapper>
                  }}
                />
                }
              </Switch>
            </BrowserRouter>
          </ThemeProvider>
        </ApolloProvider>
      );
    }
  }
);

const Wrapper = styled.div`
  display: flex;
  position: absolute;
  min-width: 800px;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.moduleBackground};
`;

const Mod = styled.div`
  display: flex;
  flex: 1;
  padding: 10px;
  background-color: #ebeef7;
`;
const Border = styled.div`
  display: flex;
  flex: 1;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${props => props.theme.panelBackground};
  box-shadow: ${props => props.theme.shadow};
`;



// WEBPACK FOOTER //
// ./src/AppWithStore.js